import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
import { FormsTemplateService } from '../services';
import {
  CancelTemplatePage,
  ClearTemplateError,
  CreateTemplate,
  CreateTemplateOnServer,
  UpdateTemplate,
  UpdateTemplateOnServer,
  UpdateTemplateStatusOnServer,
  LoadTemplatePage,
  TemplateActionTypes,
  RequestTemplatePage,
  ThrowTemplateError,
  ToggleTemplateActionLoading,
  ToggleTemplatePageLoading,
} from '../actions';

@Injectable()
export class TemplateEffects {
  showPageLoading = new ToggleTemplatePageLoading({ pageLoading: true });
  hidePageLoading = new ToggleTemplatePageLoading({ pageLoading: false });
  showActionLoading = new ToggleTemplateActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleTemplateActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private templateService: FormsTemplateService,
  ) {}

  @Effect()
  loadTemplatePage$ = this.actions$.pipe(
    ofType<RequestTemplatePage>(TemplateActionTypes.RequestTemplatePage),
    map((action: RequestTemplatePage) => action.payload),
    mergeMap(({ query }) => {
      this.store.dispatch(this.showPageLoading);
      const queryResult$ = this.templateService.getTemplatePage(query);
      const observables = [queryResult$, of(query)];

      return forkJoin(observables).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.length) {
            this.store.dispatch(new ClearTemplateError());
            this.store.dispatch(this.hidePageLoading);

            return new LoadTemplatePage({
              templates: res[0].data,
              totalCount: res[0].count,
              query: res[1],
            });
          }

          this.store.dispatch(new CancelTemplatePage());

          return new ThrowTemplateError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  createTemplate$ = this.actions$.pipe(
    ofType<CreateTemplateOnServer>(TemplateActionTypes.CreateTemplateOnServer),
    map((action: CreateTemplateOnServer) => action.payload),
    mergeMap(({ template }) => {
      this.store.dispatch(this.showActionLoading);

      return this.templateService.createTemplate(template).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearTemplateError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateTemplate({ template: res.body?.data });
          }

          return new ThrowTemplateError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateTemplate$ = this.actions$.pipe(
    ofType<UpdateTemplateOnServer>(TemplateActionTypes.UpdateTemplateOnServer),
    map((action: UpdateTemplateOnServer) => action.payload),
    mergeMap(({ template }) => {
      this.store.dispatch(this.showActionLoading);

      return this.templateService.updateTemplate(template).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearTemplateError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateTemplate({ template: res.body?.data });
          }

          return new ThrowTemplateError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateTemplateStatus$ = this.actions$.pipe(
    ofType<UpdateTemplateStatusOnServer>(TemplateActionTypes.UpdateTemplateStatusOnServer),
    map((action: UpdateTemplateStatusOnServer) => action.payload),
    mergeMap(({ template }) => {
      this.store.dispatch(this.showActionLoading);

      return this.templateService.updateTemplateStatus(template).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearTemplateError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateTemplate({ template: template as any });
          }

          return new ThrowTemplateError({ error: res });
        }),
      );
    }),
  );
}
